import React, {
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "../styles/video.css";
import play from "../assets/icons/play-video.svg";
import waterMark from "../assets/icons/water-mark-gist.png";
import styled from "styled-components";
import PropTypes from "prop-types";
import VideoLoader from "./VideoLoader";

const backgroundMusicMapper = {
  nuetral:
    "https://firebasestorage.googleapis.com/v0/b/colonoscopy-dev.appspot.com/o/GistMD%20Nuetral%20Soundtrack%20-%20Complete%20Master%20for%20Dialogue.mp3?alt=media&token=1fcfc68b-0166-435f-87cc-559a1b40b53b",
  optimistic:
    "https://firebasestorage.googleapis.com/v0/b/colonoscopy-dev.appspot.com/o/GistMD%20Optimistic%20Soundtrack.mp3?alt=media&token=861b876c-7c3a-4af8-969f-472abdf88a50",
  serious:
    "https://firebasestorage.googleapis.com/v0/b/colonoscopy-dev.appspot.com/o/GistMD%20Serious%20Soundtrack%20-%20Complete%20Master%20for%20Dialogue.mp3?alt=media&token=b8d1d53d-8ec2-49b3-9730-2507958bf854",
};

const defaultBackgroundMusic = "nuetral";

export const Player = ({
  src,
  autoFullScreen,
  passedRef,
  thumbnail,
  audioStartDelay,
  onLocationUpdate,
  backgroundMusic,
  onLoadMetadata,
  onPlayerPlaying,
  backgroundMusicVolume,
  showGistmdWaterMark = true,
  ...props
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const audioRef = useRef(null);
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  const options = useMemo(
    () => ({
      controls: true,
      responsive: true,
      fluid: true,
      sources: [
        {
          src,
          type: "application/x-mpegURL",
        },
      ],
      poster: thumbnail,
    }),
    [src, thumbnail]
  );

  const onPlayerPlay = () => {
    onPlayerPlaying && onPlayerPlaying();
  };

  const onPlayerReady = () => {
    setIsVideoLoading(false);
  };

  const onPlayerPause = () => {
    // When pausing the video pause the background music.
    audioRef.current.pause();
  };

  // Initallizing timer to edit the amount of time for the function to be trigger using the videoJs hook.
  const timer = useRef(0);
  const secondsDelta = 3;
  const onPlayerUpdateTime = useCallback(() => {
    const percentage =
      (playerRef.current.currentTime() / playerRef.current.duration()) * 100;
    const location = playerRef.current.currentTime();
    if (location >= audioStartDelay && !playerRef.current.paused())
      audioRef.current.play();
    if (location > timer.current) {
      timer.current += secondsDelta;
      if (onLocationUpdate) onLocationUpdate(percentage, location);
    }
  }, [audioStartDelay, onLocationUpdate]);

  const onPlayerSeeking = useCallback(() => {
    // On seeking we want to update the timer of the updateTime trigger to keep triggering after every 3 seconds and not less.
    const location = playerRef.current.currentTime();
    // On scroll to another location in the video change the music location also.
    audioRef.current.currentTime = location;
    // Taking the maximum to handle backwards seeking.
    timer.current = Math.max(timer.current, location);
  }, []);

  const setPlayerInitialSettings = useCallback(
    (element) => {
      // audio.current.volume = props.audioVolume;
      // set the props of the player:
      element.on("timeupdate", onPlayerUpdateTime);
      element.on("ready", onPlayerReady);
      element.on("pause", onPlayerPause);
      element.on("seeking", onPlayerSeeking);
      element.on("play", onPlayerPlay);
      onLoadMetadata && element.on("loadedmetadata", onLoadMetadata);
    },
    [onLoadMetadata, onPlayerSeeking, onPlayerUpdateTime]
  );

  useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
      const videoElement = document.createElement("video-js");
      videoRef.current.appendChild(videoElement);
      if (!autoFullScreen) videoElement.setAttribute("playsInline", true);
      playerRef.current = videojs(videoElement, options, () =>
        setPlayerInitialSettings(playerRef.current)
      );

      const { bigPlayButton, textTrackDisplay } = playerRef.current;
      const image = document.createElement("img");
      image.style.width = "40%";
      image.setAttribute("src", play);
      // Sets the background music volume.
      audioRef.current.volume = backgroundMusicVolume;
      bigPlayButton.el_.appendChild(image);
      textTrackDisplay.on("click", () => {
        if (playerRef.current.paused()) playerRef.current.play();
        else playerRef.current.pause();
      });
      if (passedRef) passedRef.current = playerRef.current;
    } else {
      const player = playerRef.current;
      player.autoplay(options.autoplay);
      player.src(options.sources);
    }

    // Dispose the Video.js player when the functional component unmounts.
    return () => {
      setIsVideoLoading(true);
      timer.current = 0;
      const player = playerRef.current;
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
        audioRef.current?.pause();
      }
    };
  }, [autoFullScreen, options, passedRef, setPlayerInitialSettings, videoRef]);

  return (
    <VideoContainer ref={videoRef} {...props}>
      {isVideoLoading && <VideoLoader thumbnail={thumbnail} />}
      <audio src={backgroundMusicMapper[backgroundMusic]} ref={audioRef} />
      {showGistmdWaterMark &&
        <WaterMark src={waterMark} className="watermark" alt="waterMark" />
      }
    </VideoContainer>
  );
};

const VideoContainer = styled.div`
  position: relative;
`;

const WaterMark = styled.img`
  position: absolute;
  background-color: transparent;
  width: 100%;
  height: 100%;
  z-index: 2;
  inset: 0;
  pointer-events: none;
  border-radius: 15px;
`;

Player.propTypes = {
  src: PropTypes.string.isRequired,
  autoFullScreen: PropTypes.bool,
  passedRef: PropTypes.elementType,
  thumbnail: PropTypes.string,
  audioStartDelay: PropTypes.number,
  onLocationUpdate: PropTypes.func,
  backgroundMusic: PropTypes.string,
  onLoadMetadata: PropTypes.func,
  onPlayerPlaying: PropTypes.func,
  backgroundMusicVolume: PropTypes.number,
};

Player.defaultProps = {
  autoFullScreen: false,
  backgroundMusic: defaultBackgroundMusic,
  backgroundMusicVolume: 0.5,
};
